import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, CheckCircle2, Clock, AlertTriangle, Bell, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import ShareWhatsappButton from './ShareWhatsappButton';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'long' });
}

function formatRange(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  const dayFmt = { day: '2-digit', month: 'long' };
  return `${s.toLocaleDateString('es-AR', dayFmt)} — ${new Date(e.getTime() - 1).toLocaleDateString('es-AR', dayFmt)}`;
}

function buildWhatsAppSummary(summary, userName) {
  const lines = [];
  lines.push(`📊 Mi Resumen Semanal — ${userName || ''}`.trim());
  lines.push(`🗓️ ${formatRange(summary.period.start, summary.period.end)}`);
  lines.push('');
  const c = summary.counts;
  lines.push(`✅ Completadas: ${c.completed_this_week}`);
  lines.push(`⏳ Próximas: ${c.upcoming_tasks + c.upcoming_reminders}`);
  if (c.overdue_tasks + c.overdue_reminders > 0) {
    lines.push(`⚠️ Atrasadas: ${c.overdue_tasks + c.overdue_reminders}`);
  }

  if (summary.upcoming_tasks.length || summary.upcoming_reminders.length) {
    lines.push('');
    lines.push('📌 Esta semana:');
    summary.upcoming_tasks.slice(0, 5).forEach((t) => {
      lines.push(`• ${t.title}${t.due_date ? ` (${formatDate(t.due_date)})` : ''}`);
    });
    summary.upcoming_reminders.slice(0, 5).forEach((r) => {
      lines.push(`• ${r.title}${r.remind_at ? ` (${formatDate(r.remind_at)})` : ''}`);
    });
  }

  lines.push('');
  lines.push('— Vida en Orden');
  return lines.join('\n');
}

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <div
    data-testid={testId}
    className={`p-4 rounded-2xl border ${color.bg} ${color.border}`}
  >
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 ${color.icon}`} strokeWidth={2} />
      <span className={`text-xs font-semibold uppercase tracking-wider ${color.text}`}>{label}</span>
    </div>
    <p className={`text-3xl font-bold ${color.text}`} >{value}</p>
  </div>
);

// Palette constants: kept at module scope so they are not recreated on every render.
const STAT_COLOR_COMPLETED = { bg: 'bg-emerald-100 dark:bg-emerald-500/15', border: 'border-[#4CAF50]/30', icon: 'text-emerald-600 dark:text-emerald-400', text: 'text-foreground' };
const STAT_COLOR_UPCOMING  = { bg: 'bg-sky-100 dark:bg-sky-500/15', border: 'border-foreground/20', icon: 'text-foreground', text: 'text-foreground' };
const STAT_COLOR_OVERDUE   = { bg: 'bg-amber-100 dark:bg-amber-500/15', border: 'border-[#D97706]/30', icon: 'text-primary', text: 'text-amber-800 dark:text-amber-300' };
const STAT_COLOR_ALERTS    = { bg: 'bg-[#FDE68A]/40', border: 'border-[#D97706]/20', icon: 'text-amber-800 dark:text-amber-300', text: 'text-amber-800 dark:text-amber-300' };

const SECTION_COLORS_OVERDUE  = { icon: 'text-primary', badge: 'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300', row: 'bg-amber-100 dark:bg-amber-500/15/60' };
const SECTION_COLORS_TASKS    = { icon: 'text-foreground', badge: 'bg-sky-100 dark:bg-sky-500/15 text-foreground', row: 'bg-surface-2' };
const SECTION_COLORS_REMS     = { icon: 'text-amber-800 dark:text-amber-300', badge: 'bg-[#FDE68A]/60 text-amber-800 dark:text-amber-300', row: 'bg-amber-100 dark:bg-amber-500/10' };
const SECTION_COLORS_DONE     = { icon: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400', row: 'bg-emerald-100 dark:bg-emerald-500/15/60' };

const Section = ({ icon: Icon, title, items, colorClasses, isReminder, testId }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-6" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${colorClasses.icon}`} strokeWidth={1.5} />
        <h3 className="font-bold text-foreground">{title}</h3>
        <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${colorClasses.badge}`}>
          {items.length}
        </span>
      </div>
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className={`flex items-center gap-3 p-3 rounded-lg ${colorClasses.row}`}
          >
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground truncate">{item.title}</p>
              {(item.due_date || item.remind_at) && (
                <p className="text-xs text-muted-foreground">
                  {formatDate(item.due_date || item.remind_at)}
                </p>
              )}
            </div>
            {(item.due_date || item.remind_at) && (
              <ShareWhatsappButton
                title={item.title}
                date={item.due_date || item.remind_at}
                description={item.description}
                kind={isReminder ? (item.type === 'payment' ? 'bill' : 'reminder') : 'task'}
                testId={`share-summary-${item.id}`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function WeeklySummaryModal({ onClose, userName }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/summary/weekly`, { withCredentials: true });
        setSummary(data);
      } catch (error) {
        toast.error('No pudimos cargar tu resumen');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleShareAll = () => {
    if (!summary) return;
    const text = buildWhatsAppSummary(summary, userName);
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const total = summary
    ? summary.counts.completed_this_week + summary.counts.upcoming_tasks + summary.counts.upcoming_reminders + summary.counts.overdue_tasks + summary.counts.overdue_reminders
    : 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-surface border-b border-black/5 p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-foreground" >
              Mi Resumen Semanal
            </h2>
            {summary && (
              <p className="text-sm text-muted-foreground mt-1" data-testid="summary-period">
                {formatRange(summary.period.start, summary.period.end)}
              </p>
            )}
          </div>
          <button
            data-testid="close-summary-modal-btn"
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-all"
          >
            <X className="w-6 h-6 text-muted-foreground" />
          </button>
        </div>

        <div className="p-6">
          {loading && (
            <div className="text-center py-12" data-testid="summary-loading">
              <div className="w-12 h-12 border-4 border-foreground border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Preparando tu resumen...</p>
            </div>
          )}

          {!loading && summary && total === 0 && (
            <div className="text-center py-12" data-testid="summary-empty">
              <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-muted-foreground text-lg">Semana en calma</p>
              <p className="text-muted-foreground text-sm mt-2">No hay pendientes ni vencimientos que reportar. ¡Aprovechá para respirar!</p>
            </div>
          )}

          {!loading && summary && total > 0 && (
            <>
              {/* Stats grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="summary-stats">
                <StatCard
                  testId="stat-completed"
                  icon={CheckCircle2}
                  label="Hechas"
                  value={summary.counts.completed_this_week}
                  color={STAT_COLOR_COMPLETED}
                />
                <StatCard
                  testId="stat-upcoming"
                  icon={Clock}
                  label="Próximas"
                  value={summary.counts.upcoming_tasks + summary.counts.upcoming_reminders}
                  color={STAT_COLOR_UPCOMING}
                />
                <StatCard
                  testId="stat-overdue"
                  icon={AlertTriangle}
                  label="Atrasadas"
                  value={summary.counts.overdue_tasks + summary.counts.overdue_reminders}
                  color={STAT_COLOR_OVERDUE}
                />
                <StatCard
                  testId="stat-reminders"
                  icon={Bell}
                  label="Alertas"
                  value={summary.counts.upcoming_reminders + summary.counts.overdue_reminders}
                  color={STAT_COLOR_ALERTS}
                />
              </div>

              {/* Sections */}
              <Section
                testId="section-overdue-tasks"
                icon={AlertTriangle}
                title="Tareas atrasadas"
                items={summary.overdue_tasks}
                colorClasses={SECTION_COLORS_OVERDUE}
              />
              <Section
                testId="section-overdue-reminders"
                icon={AlertTriangle}
                title="Vencimientos atrasados"
                items={summary.overdue_reminders}
                isReminder
                colorClasses={SECTION_COLORS_OVERDUE}
              />
              <Section
                testId="section-upcoming-tasks"
                icon={Clock}
                title="Tareas de la semana"
                items={summary.upcoming_tasks}
                colorClasses={SECTION_COLORS_TASKS}
              />
              <Section
                testId="section-upcoming-reminders"
                icon={Bell}
                title="Vencimientos próximos"
                items={summary.upcoming_reminders}
                isReminder
                colorClasses={SECTION_COLORS_REMS}
              />
              <Section
                testId="section-completed"
                icon={CheckCircle2}
                title="Ya lo hiciste"
                items={summary.completed_this_week}
                colorClasses={SECTION_COLORS_DONE}
              />

              {/* Share whole summary */}
              <button
                data-testid="share-summary-btn"
                onClick={handleShareAll}
                className="w-full mt-4 py-3 bg-emerald-500 text-white rounded-full font-semibold hover:bg-[#1EBE5D] transition-all hover:-translate-y-0.5 shadow-lg flex items-center justify-center gap-2"
              >
                Compartir resumen con mi familia
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
